import { motion } from "framer-motion";
import { FileText, ShieldCheck, FlaskConical, Download } from "lucide-react";
import { useLang } from "@/context/LangContext";

const LAB_CONTENT = {
  tr: {
    eyebrow: "— Lab Raporu",
    title: "Bağımsız laboratuvar\nsonuçlarıyla kanıtlı.",
    body: "PAKLA formülü, T.C. Tarım ve Orman Bakanlığı akredite laboratuvarlarında pestisit, bakteri ve kimyasal kalıntı arındırma performansı için test edilmiştir. Sonuçları ve sertifikaları aşağıdan inceleyebilirsiniz.",
    cards: [
      { title: "Pestisit Arındırma", value: "%99,4", meta: "Test parametresi" },
      { title: "Bakteri Azaltımı", value: "%99,8", meta: "E. coli / Salmonella" },
      { title: "Kalıntı Bırakma", value: "0", meta: "Durulama sonrası" },
    ],
    download: "Lab Raporunu İndir (PDF)",
    note: "Raporun tam metnini indirmek için tıklayın. Sertifika numarası: PKL-2026-01",
  },
  en: {
    eyebrow: "— Lab Report",
    title: "Backed by independent\nlaboratory results.",
    body: "PAKLA's formula has been tested at T.C. Ministry of Agriculture-accredited laboratories for pesticide, bacteria and chemical residue removal performance. Review the results and certificates below.",
    cards: [
      { title: "Pesticide Removal", value: "99.4%", meta: "Test parameter" },
      { title: "Bacteria Reduction", value: "99.8%", meta: "E. coli / Salmonella" },
      { title: "No Residue", value: "0", meta: "After rinsing" },
    ],
    download: "Download Lab Report (PDF)",
    note: "Click to download the full report. Certificate no: PKL-2026-01",
  },
  el: {
    eyebrow: "— Εργαστηριακή Αναφορά",
    title: "Πιστοποιημένα από\nανεξάρτητο εργαστήριο.",
    body: "Η φόρμουλα του PAKLA έχει δοκιμαστεί σε διαπιστευμένα εργαστήρια του Τουρκικού Υπουργείου Γεωργίας για την απομάκρυνση φυτοφαρμάκων, βακτηρίων και χημικών υπολειμμάτων.",
    cards: [
      { title: "Απομάκρυνση Φυτοφαρμάκων", value: "99,4%", meta: "Παράμετρος δοκιμής" },
      { title: "Μείωση Βακτηρίων", value: "99,8%", meta: "E. coli / Salmonella" },
      { title: "Χωρίς Κατάλοιπα", value: "0", meta: "Μετά το ξέπλυμα" },
    ],
    download: "Λήψη Αναφοράς (PDF)",
    note: "Αριθμός πιστοποιητικού: PKL-2026-01",
  },
};

const REPORT_URL = "/lab/pakla-lab-report.pdf";

const CERTIFICATES = [
  {
    src: "https://customer-assets-39nsmqrw.emergentagent.net/job_pakla-wash/artifacts/hj34bko5_image.png",
    title: "TS EN ISO 9001:2015",
    subtitle: "TCS Belgelendirme · Kalite Yönetim Sistemi",
    no: "QM 250163-TR",
  },
  {
    src: "https://customer-assets-39nsmqrw.emergentagent.net/job_pakla-wash/artifacts/vw503g30_image.png",
    title: "CE Uygunluk Beyanı",
    subtitle: "Belcert · Ürün Güvenlik Yönetmeliği (GPSR)",
    no: "A1808361",
  },
  {
    src: "https://customer-assets-39nsmqrw.emergentagent.net/job_pakla-wash/artifacts/5uj92kx0_image.png",
    title: "ISO 45001:2018",
    subtitle: "Belcert · İş Sağlığı ve Güvenliği Yönetim Sistemi",
    no: "A1822389",
  },
];

const ANALYSIS_REPORTS = [
  {
    src: "https://customer-assets-39nsmqrw.emergentagent.net/job_pakla-wash/artifacts/uif057wp_image.png",
    title: "Pestisit Analizi · No. 1",
    subtitle: "T.C. Tarım ve Orman Bakanlığı · Denise Özel Gıda Kontrol Lab.",
    no: "2026-1772 · 22.05.2026",
  },
  {
    src: "https://customer-assets-39nsmqrw.emergentagent.net/job_pakla-wash/artifacts/0s6xlxfc_image.png",
    title: "Pestisit Analizi · No. 2",
    subtitle: "T.C. Tarım ve Orman Bakanlığı · Denise Özel Gıda Kontrol Lab.",
    no: "2026-2027 · 11.06.2026",
  },
  {
    src: "https://customer-assets-39nsmqrw.emergentagent.net/job_pakla-wash/artifacts/sw3spi9p_image.png",
    title: "Pestisit Analizi · No. 3",
    subtitle: "T.C. Tarım ve Orman Bakanlığı · Denise Özel Gıda Kontrol Lab.",
    no: "2026-2029 · 11.06.2026",
  },
  {
    src: "https://customer-assets-39nsmqrw.emergentagent.net/job_pakla-wash/artifacts/79vtna8e_image.png",
    title: "Pestisit Analizi · No. 4",
    subtitle: "T.C. Tarım ve Orman Bakanlığı · Denise Özel Gıda Kontrol Lab.",
    no: "2026-2034 · 11.06.2026",
  },
];

const LabReport = () => {
  const { lang } = useLang();
  const c = LAB_CONTENT[lang] || LAB_CONTENT.tr;

  return (
    <section
      id="lab"
      data-testid="lab-section"
      className="relative py-24 md:py-32 lg:py-40 bg-[#F2EEE3]"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-12 gap-8 md:gap-12 items-end mb-14 md:mb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.9 }}
            className="col-span-12 md:col-span-7"
          >
            <div className="font-eyebrow text-2xl md:text-[30px] text-[#0F3D2E]/85 mb-6">{c.eyebrow}</div>
            <h2 className="font-serif-editorial text-[#0F3D2E] leading-[0.98] text-[8vw] sm:text-[6.4vw] md:text-[4.4vw] lg:text-[3.4vw]">
              {c.title.split("\n").map((l, i) => (
                <span key={i} className="block">
                  {i === 1 ? <em className="italic text-[#2E8B4A]">{l}</em> : l}
                </span>
              ))}
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.9, delay: 0.1 }}
            className="col-span-12 md:col-span-4 md:col-start-9"
          >
            <div className="hairline mb-6" />
            <p className="font-ubuntu text-[16px] md:text-[17px] leading-relaxed text-[#0A1F17]">
              {c.body}
            </p>
          </motion.div>
        </div>

        {/* Metric cards */}
        <div className="grid grid-cols-12 gap-4 md:gap-6 mb-10">
          {c.cards.map((card, i) => {
            const Icon = [ShieldCheck, FlaskConical, FileText][i] || FileText;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ delay: i * 0.1, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                className="col-span-12 md:col-span-4 bg-[#FAF7F0] rounded-2xl border border-[#E5DFCE] p-8 md:p-10"
                data-testid={`lab-metric-${i}`}
              >
                <div className="flex items-start justify-between mb-8">
                  <span className="inline-flex w-12 h-12 rounded-full border border-[#0F3D2E]/25 items-center justify-center text-[#2E8B4A]">
                    <Icon size={22} strokeWidth={1.5} />
                  </span>
                  <div className="text-[10px] tracking-[0.24em] uppercase text-[#0F3D2E]/60 mt-3">
                    {card.meta}
                  </div>
                </div>
                <div className="font-products font-bold text-[#0F3D2E] text-5xl md:text-6xl leading-none mb-4">
                  {card.value}
                </div>
                <div className="font-serif-editorial text-[#0F3D2E] text-xl md:text-2xl leading-tight">
                  {card.title}
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Certificates gallery */}
        <div className="mb-14">
          <div className="flex items-center gap-4 mb-8">
            <span className="w-10 h-px bg-[#0F3D2E]/40" />
            <div className="font-eyebrow text-xl md:text-2xl text-[#0F3D2E]/85">— Sertifikalar</div>
          </div>
          <div className="grid grid-cols-12 gap-4 md:gap-6">
            {CERTIFICATES.map((cert, i) => (
              <motion.a
                key={i}
                href={cert.src}
                target="_blank"
                rel="noopener noreferrer"
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ delay: i * 0.1, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                className="col-span-12 sm:col-span-6 md:col-span-4 group bg-[#FAF7F0] rounded-2xl border border-[#E5DFCE] overflow-hidden hover:shadow-[0_20px_50px_-20px_rgba(15,61,46,0.35)] transition-all"
                data-testid={`certificate-${i}`}
              >
                <div className="relative aspect-[3/4] bg-white overflow-hidden">
                  <img
                    src={cert.src}
                    alt={cert.title}
                    className="w-full h-full object-contain p-4 group-hover:scale-[1.02] transition-transform duration-500"
                    draggable="false"
                    loading="lazy"
                  />
                </div>
                <div className="p-5 md:p-6">
                  {/* Cert label removed */}
                </div>
              </motion.a>
            ))}
          </div>
        </div>

        {/* Analysis Reports gallery */}
        <div className="mb-10">
          <div className="flex items-center gap-4 mb-8">
            <span className="w-10 h-px bg-[#0F3D2E]/40" />
            <div className="font-eyebrow text-xl md:text-2xl text-[#0F3D2E]/85">— Analiz Raporları</div>
          </div>
          <div className="grid grid-cols-12 gap-4 md:gap-6">
            {ANALYSIS_REPORTS.map((rep, i) => (
              <motion.a
                key={i}
                href={rep.src}
                target="_blank"
                rel="noopener noreferrer"
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ delay: i * 0.08, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                className="col-span-6 md:col-span-3 group bg-[#FAF7F0] rounded-2xl border border-[#E5DFCE] overflow-hidden hover:shadow-[0_20px_50px_-20px_rgba(15,61,46,0.35)] transition-all"
                data-testid={`analysis-report-${i}`}
              >
                <div className="relative aspect-[3/4] bg-white overflow-hidden">
                  <img
                    src={rep.src}
                    alt={rep.title}
                    className="w-full h-full object-contain p-3 group-hover:scale-[1.02] transition-transform duration-500"
                    draggable="false"
                    loading="lazy"
                  />
                </div>
              </motion.a>
            ))}
          </div>
        </div>

        {/* Download CTA removed */}
      </div>
    </section>
  );
};

export default LabReport;
